import React, {ReactElement, useEffect, useMemo, useState} from "react";
import {Box, CircularProgress} from "@material-ui/core";
import {Navigate, generatePath} from "react-router-dom";
import {APP_PATH, SHOW_EXHIBITOR_QUICK_CART_PATH} from "view/routes";
import {onAuthStateChanged} from "api/firebase";
import {jwtDecode} from "jwt-decode";
import {
  appendIgnoreStandardRedirect,
  getRedirectPathAfterLogin,
  quickOrderIdParamName,
} from "tools/routing";
import {
  getSessionSwitchRemainingMs,
  isSessionSwitchInProgress,
  subscribeToAuthSessionEvents,
} from "tools/auth/sessionSync";

const AnonymousRoute = ({children}: {children: ReactElement}): ReactElement => {
  const [state, setState] = useState({authorized: false, isLoading: true});

  const params = new URLSearchParams(window.location.search);
  const redirectPath = getRedirectPathAfterLogin(window.location.search);
  const token = params.get("token");

  const quickOrderPath = useMemo(() => {
    if (!token) return undefined;
    try {
      const decoded = jwtDecode<{
        showId?: string;
        exhibitorPairId?: string;
        quickOrderId?: string;
      }>(token);
      if (decoded.showId && decoded.exhibitorPairId && decoded.quickOrderId) {
        return `${generatePath(SHOW_EXHIBITOR_QUICK_CART_PATH, {
          showId: decoded.showId,
          exhibitorPairId: decoded.exhibitorPairId,
        })}?${quickOrderIdParamName}=${decoded.quickOrderId}`;
      }
    } catch {
      console.warn("Invalid token provided");
    }
    return undefined;
  }, [token]);

  useEffect(() => {
    let timeoutId: number | null = null;

    // n tabs may sit on /login simultaneously. When ONE tab actually signs
    // in (regular email/password, Admin Google callback, SSO, one-time
    // code, or impersonation handoff — every entry point calls
    // `markSessionSwitchInProgress()` before `signInWithCustomToken`), the
    // OTHER tabs would otherwise keep showing the login form for the
    // ~hundreds-of-ms window between the storage event and Firebase's own
    // cross-tab IndexedDB sync delivering the new user. Worse, a user
    // could submit a second concurrent login from a sibling tab and race
    // the backend session cookie. While the switch is in flight we hold
    // the loader and let `onAuthStateChanged(newUser)` below resolve to
    // the final navigation.
    const armSwitchTimeout = () => {
      const remainingMs = getSessionSwitchRemainingMs();

      if (!remainingMs) {
        return;
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      // If the originating tab dies mid-switch (network drops, user closes
      // it) we drop the loader after the TTL so this tab is usable again
      // — never trap the user on a blank screen forever.
      timeoutId = window.setTimeout(() => {
        setState((current) => ({...current, isLoading: false}));
      }, remainingMs + 100);
    };

    if (isSessionSwitchInProgress()) {
      setState((current) => ({...current, isLoading: true}));
      armSwitchTimeout();
    }

    const unsubscribeSessionSync = subscribeToAuthSessionEvents((event) => {
      if (event.type === "switch-start" || event.type === "login") {
        setState((current) => ({...current, isLoading: true}));
        armSwitchTimeout();
        return;
      }

      // The originating tab aborted the sign-in (bad credentials, failed
      // OAuth, etc.). Drop the loader immediately rather than waiting out
      // the 30s switch-flag TTL — the form is the right thing to show.
      if (event.type === "switch-cancel") {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }
        setState((current) => ({...current, isLoading: false}));
      }
    });

    const unsubscribeAuth = onAuthStateChanged((user) => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }

      setState({authorized: !!user, isLoading: false});
    });

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      unsubscribeAuth();
      unsubscribeSessionSync();
    };
  }, []);

  if (state.isLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  const isRegularSignUp = !!token && !quickOrderPath;
  if (isRegularSignUp) {
    return children;
  }

  if (state.authorized) {
    const path =
      quickOrderPath ||
      (redirectPath ? appendIgnoreStandardRedirect(redirectPath) : APP_PATH);
    console.warn("[DIAG] AnonymousRoute -> Navigate", {
      redirectPath,
      quickOrderPath,
      path,
      search: window.location.search,
    });
    return <Navigate to={path} />;
  }
  return children;
};

export default AnonymousRoute;
